import React, { useEffect, useState } from "react";
import api, { apiErrorMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Save, Trash2, Plus, ArrowUp, ArrowDown } from "lucide-react";
import { useBrand } from "../lib/BrandContext";

const emptyBanner = () => ({ title: "", subtitle: "", image: "", cta_text: "", cta_link: "" });
const emptySection = () => ({ key: "", title: "", body: "", enabled: true });

const SECTION_KEYS = [
  "premium_deals",
  "top_picks",
  "listings",
  "success_stories",
  "projects",
  "testimonials",
];
const SECTION_LABEL = {
  premium_deals: "Premium Deals",
  top_picks: "Best Deals (Top Picks)",
  listings: "All Listings + Filter (core)",
  success_stories: "Success Stories",
  projects: "Projects CTA Banner",
  testimonials: "Testimonials Gallery",
};

export default function AdminContentTab() {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const { refresh: refreshBrand } = useBrand();

  const load = () => api.get("/content").then((r) => setC(r.data));

  useEffect(() => { load(); }, []);

  const update = (k, v) => setC((s) => ({ ...s, [k]: v }));

  const updateTheme = (k, v) =>
    setC((s) => ({ ...s, theme: { ...(s.theme || {}), [k]: v } }));

  // v2.59 — Access Page editor helpers
  const updateAccess = (k, v) =>
    setC((s) => ({ ...s, access_page: { ...(s.access_page || {}), [k]: v } }));
  const updateAccessRole = (role, k, v) =>
    setC((s) => ({
      ...s,
      access_page: {
        ...(s.access_page || {}),
        [role]: { ...((s.access_page || {})[role] || {}), [k]: v },
      },
    }));

  // v2.64 — Footer editor helpers
  const updateFooter = (k, v) =>
    setC((s) => ({ ...s, footer: { ...(s.footer || {}), [k]: v } }));

  // v2.66 — Plans Preview editor helpers
  const updatePP = (k, v) =>
    setC((s) => ({ ...s, plans_preview: { ...(s.plans_preview || {}), [k]: v } }));
  const updateFooterSocial = (k, v) =>
    setC((s) => ({
      ...s,
      footer: {
        ...(s.footer || {}),
        social: { ...((s.footer || {}).social || {}), [k]: v },
      },
    }));
  const updateFooterQuickLink = (idx, k, v) =>
    setC((s) => {
      const links = [...((s.footer || {}).quick_links || [])];
      links[idx] = { ...(links[idx] || {}), [k]: v };
      return { ...s, footer: { ...(s.footer || {}), quick_links: links } };
    });
  const addFooterQuickLink = () =>
    setC((s) => ({
      ...s,
      footer: { ...(s.footer || {}), quick_links: [...((s.footer || {}).quick_links || []), { label: "", href: "" }] },
    }));
  const removeFooterQuickLink = (idx) =>
    setC((s) => ({
      ...s,
      footer: { ...(s.footer || {}), quick_links: ((s.footer || {}).quick_links || []).filter((_, i) => i !== idx) },
    }));

  const moveSection = (key, dir) => {
    const order = [...(c.section_order || SECTION_KEYS)];
    const i = order.indexOf(key);
    if (i < 0) return;
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    update("section_order", order);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/content", c);
      toast.success("Content saved");
      await load();
      await refreshBrand();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Save failed"));
    }
    setBusy(false);
  };

  if (!c) return <div className="p-8 text-[#A1A1AA]">Loading content…</div>;

  return (
    <div className="space-y-6" data-testid="admin-content-tab">
      {/* Hero */}
      <Section title="Hero / Headline">
        <Field label="Eyebrow" value={c.hero_eyebrow || ""} onChange={(v) => update("hero_eyebrow", v)} testid="content-hero-eyebrow" />
        <Field label="Headline (line 1)" value={c.hero_headline || ""} onChange={(v) => update("hero_headline", v)} testid="content-hero-headline" />
        <Field label="Headline accent (line 2, blue)" value={c.hero_headline_accent || ""} onChange={(v) => update("hero_headline_accent", v)} testid="content-hero-accent" />
        <Field label="Headline tail (line 3, muted)" value={c.hero_headline_tail || ""} onChange={(v) => update("hero_headline_tail", v)} testid="content-hero-tail" />
        <FieldArea label="Subline" value={c.hero_subline || ""} onChange={(v) => update("hero_subline", v)} testid="content-hero-subline" />
        <FieldArea label="Tagline" value={c.hero_tagline || ""} onChange={(v) => update("hero_tagline", v)} testid="content-hero-tagline" />
      </Section>

      {/* v2.59 — Access Page (3-role onboarding) */}
      <Section title="Access Page · Onboarding (Client / Dealer / Builder / Owner)">
        <div className="bg-[#EEF4FF] border border-[#C7D9FF] text-[12px] text-[#0044CC] p-3 rounded-sm mb-3">
          Yeh page <code className="font-mono bg-white px-1.5 py-0.5 border border-[#C7D9FF] rounded-sm">/access</code> par dikhta hai. Naye users yahaan se signup/login karte hain. Saare texts, button labels — sab editable hain.
        </div>

        <div className="grid lg:grid-cols-3 gap-3">
          <Field label="Eyebrow Pill" value={c.access_page?.eyebrow || ""} onChange={(v) => updateAccess("eyebrow", v)} testid="content-access-eyebrow" />
          <div className="lg:col-span-2">
            <Field label="Heading" value={c.access_page?.heading || ""} onChange={(v) => updateAccess("heading", v)} testid="content-access-heading" />
          </div>
        </div>
        <FieldArea label="Sub-text" value={c.access_page?.sub || ""} onChange={(v) => updateAccess("sub", v)} testid="content-access-sub" />

        <label className="flex items-center gap-2 text-[12.5px] text-[#09090B] mt-2 cursor-pointer">
          <input
            type="checkbox"
            checked={c.access_page?.show_top_signin !== false}
            onChange={(e) => updateAccess("show_top_signin", e.target.checked)}
            className="accent-[#0055FF]"
            data-testid="access-show-top-signin"
          />
          Show <b className="mx-1">"Already have an account? Sign in"</b> link at top of page
        </label>

        <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-4 mt-3">
          {[
            { key: "client", label: "Client Card", color: "border-[#C7D9FF] bg-[#EEF4FF]" },
            { key: "dealer", label: "Dealer Card", color: "border-[#C7D9FF] bg-gradient-to-br from-[#EEF4FF] to-white" },
            { key: "builder", label: "Builder / Owner Card", color: "border-[#DDD6FE] bg-[#F5F3FF]" },
            { key: "team", label: "Team Card", color: "border-[#BBF7D0] bg-[#F0FDF4]" },
          ].map(({ key, label, color }) => {
            const card = c.access_page?.[key] || {};
            return (
              <div key={key} className={`border ${color} rounded-sm p-3 space-y-2`} data-testid={`access-card-${key}-editor`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-[#09090B]">{label}</div>
                  <label className="text-[11px] inline-flex items-center gap-1.5 cursor-pointer text-[#52525B]">
                    <input
                      type="checkbox"
                      checked={card.enabled !== false}
                      onChange={(e) => updateAccessRole(key, "enabled", e.target.checked)}
                      className="accent-[#0055FF]"
                      data-testid={`access-card-${key}-enabled`}
                    />
                    Show on Access page
                  </label>
                </div>
                <Field label="Title" value={card.title || ""} onChange={(v) => updateAccessRole(key, "title", v)} testid={`access-card-${key}-title`} />
                <FieldArea label="Sub-text" value={card.subtext || ""} onChange={(v) => updateAccessRole(key, "subtext", v)} testid={`access-card-${key}-subtext`} />
                <Field label="Primary (Register) Button Label" value={card.primary_label || ""} onChange={(v) => updateAccessRole(key, "primary_label", v)} testid={`access-card-${key}-primary`} />
                <Field label="Secondary (Login) Button Label" value={card.secondary_label || ""} onChange={(v) => updateAccessRole(key, "secondary_label", v)} testid={`access-card-${key}-secondary`} />
              </div>
            );
          })}
        </div>
      </Section>

      {/* Hero spacer ends — Theme & Brand below */}

      {/* v2.66 — Plans Preview editor (powers /plans-preview share page) */}
      <Section title="Plans Preview · Public Share Page (used by Team's Share Plans)">
        <div className="bg-[#FFFBEB] border border-[#FDE68A] text-[12px] text-[#92400E] p-3 rounded-sm mb-3">
          Yeh page <code className="font-mono bg-white px-1.5 py-0.5 border border-[#FDE68A] rounded-sm">/plans-preview</code> par dikhta hai. Team isi link ko WhatsApp pe leads ko bhejti hai. Yahaan se hero, CTA aur button labels customize karein.
        </div>

        <div className="text-[11px] uppercase tracking-wider font-bold text-[#09090B] mb-2">Hero (top of page)</div>
        <div className="grid lg:grid-cols-2 gap-3">
          <Field label="Eyebrow Pill" value={c.plans_preview?.hero_eyebrow || ""} onChange={(v) => updatePP("hero_eyebrow", v)} testid="pp-hero-eyebrow" />
          <Field label="Heading (regular text)" value={c.plans_preview?.hero_heading_a || ""} onChange={(v) => updatePP("hero_heading_a", v)} testid="pp-hero-heading-a" />
          <Field label="Heading Accent (blue text)" value={c.plans_preview?.hero_heading_b || ""} onChange={(v) => updatePP("hero_heading_b", v)} testid="pp-hero-heading-b" />
        </div>
        <FieldArea label="Hero Sub-text" value={c.plans_preview?.hero_sub || ""} onChange={(v) => updatePP("hero_sub", v)} testid="pp-hero-sub" />

        <div className="text-[11px] uppercase tracking-wider font-bold text-[#09090B] mb-2 mt-5">Bottom CTA Block</div>
        <div className="grid lg:grid-cols-2 gap-3">
          <Field label="CTA Eyebrow" value={c.plans_preview?.cta_eyebrow || ""} onChange={(v) => updatePP("cta_eyebrow", v)} testid="pp-cta-eyebrow" />
          <Field label="CTA Heading" value={c.plans_preview?.cta_heading || ""} onChange={(v) => updatePP("cta_heading", v)} testid="pp-cta-heading" />
        </div>
        <FieldArea label="CTA Sub-text" value={c.plans_preview?.cta_sub || ""} onChange={(v) => updatePP("cta_sub", v)} testid="pp-cta-sub" />

        <div className="grid lg:grid-cols-2 gap-3">
          <Field label='"Create Account" Button Label' value={c.plans_preview?.cta_create_label || ""} onChange={(v) => updatePP("cta_create_label", v)} testid="pp-cta-create" />
          <Field label='"WhatsApp" Button Label' value={c.plans_preview?.cta_whatsapp_label || ""} onChange={(v) => updatePP("cta_whatsapp_label", v)} testid="pp-cta-wa" />
        </div>
        <FieldArea label="WhatsApp Pre-filled Message (when client clicks WA button)" value={c.plans_preview?.cta_whatsapp_message || ""} onChange={(v) => updatePP("cta_whatsapp_message", v)} testid="pp-cta-wa-msg" />

        <Field label="Footnote (bottom secure-payments line)" value={c.plans_preview?.footnote || ""} onChange={(v) => updatePP("footnote", v)} testid="pp-footnote" />
      </Section>

      {/* v2.64 — Footer editor */}
      <Section title="Footer · Company info, Socials & Links">
        <div className="bg-[#F0FDF4] border border-[#BBF7D0] text-[12px] text-[#15803D] p-3 rounded-sm mb-3">
          Yeh sab niche site ke footer me dikhega. Social links bare handles bhi accept karte hain (e.g. <code className="font-mono bg-white px-1.5 py-0.5 border border-[#BBF7D0] rounded-sm">@houseentry</code> for Instagram/X) ya full URL.
        </div>

        <FieldArea label="About / Tagline (paragraph)" value={c.footer?.about_text || ""} onChange={(v) => updateFooter("about_text", v)} testid="footer-about-text" />

        <div className="grid lg:grid-cols-2 gap-3">
          <Field label="Office Address" value={c.footer?.address || ""} onChange={(v) => updateFooter("address", v)} testid="footer-address" />
          <Field label="Phone (with country code)" value={c.footer?.phone || ""} onChange={(v) => updateFooter("phone", v)} testid="footer-phone" />
          <Field label="Email" value={c.footer?.email || ""} onChange={(v) => updateFooter("email", v)} testid="footer-email" />
          <Field label="RERA / Licence ID" value={c.footer?.rera_id || ""} onChange={(v) => updateFooter("rera_id", v)} testid="footer-rera" />
        </div>
        <Field
          label="Copyright line (use {year} for current year)"
          value={c.footer?.copyright || ""}
          onChange={(v) => updateFooter("copyright", v)}
          testid="footer-copyright"
        />

        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#09090B] mb-2">Social Media Links</div>
          <div className="grid lg:grid-cols-2 gap-3">
            {[
              ["facebook", "Facebook"],
              ["instagram", "Instagram"],
              ["youtube", "YouTube"],
              ["linkedin", "LinkedIn"],
              ["twitter", "X (Twitter)"],
              ["whatsapp", "WhatsApp (10-digit number or wa.me URL)"],
              ["telegram", "Telegram (@handle or full URL)"],
            ].map(([key, label]) => (
              <Field
                key={key}
                label={label}
                value={(c.footer?.social || {})[key] || ""}
                onChange={(v) => updateFooterSocial(key, v)}
                testid={`footer-social-${key}-input`}
                placeholder={key === "whatsapp" ? "919812345678" : "@handle  or  https://..."}
              />
            ))}
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider font-bold text-[#09090B]">Quick Links Column</div>
            <Button size="sm" variant="outline" onClick={addFooterQuickLink} className="rounded-sm h-7 text-[11px]" data-testid="footer-add-quick-link">+ Add Link</Button>
          </div>
          <div className="space-y-2">
            {(c.footer?.quick_links || []).map((q, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center" data-testid={`footer-quick-link-${i}`}>
                <Input
                  value={q.label || ""}
                  onChange={(e) => updateFooterQuickLink(i, "label", e.target.value)}
                  placeholder="Label (e.g. Pricing)"
                  className="rounded-sm h-9 text-[12px]"
                  data-testid={`footer-quick-link-${i}-label`}
                />
                <Input
                  value={q.href || ""}
                  onChange={(e) => updateFooterQuickLink(i, "href", e.target.value)}
                  placeholder="URL (e.g. /pricing  or  https://...)"
                  className="rounded-sm h-9 text-[12px]"
                  data-testid={`footer-quick-link-${i}-href`}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeFooterQuickLink(i)}
                  className="rounded-sm h-9 text-[#DC2626] hover:bg-[#FEE2E2]"
                  data-testid={`footer-quick-link-${i}-remove`}
                >
                  Remove
                </Button>
              </div>
            ))}
            {(c.footer?.quick_links || []).length === 0 && (
              <div className="text-[12px] text-[#A1A1AA] italic">No quick links — defaults will be used.</div>
            )}
          </div>
        </div>
      </Section>

      {/* Featured Areas */}
      <Section title="Featured Areas (comma-separated)">
        <Field
          label="Areas"
          value={(c.featured_areas || []).join(", ")}
          onChange={(v) => update("featured_areas", v.split(",").map((s) => s.trim()).filter(Boolean))}
          testid="content-areas"
        />
      </Section>

      {/* Theme & Brand */}
      <Section title="Brand · Theme & Logo">
        <div className="grid lg:grid-cols-2 gap-3" data-testid="theme-editor">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-1">Logo URL</div>
            <Input
              value={c.theme?.logo_url || ""}
              onChange={(e) => updateTheme("logo_url", e.target.value)}
              placeholder="https://..."
              className="rounded-sm"
              data-testid="theme-logo-url"
            />
            {c.theme?.logo_url && (
              <div className="mt-2 inline-flex items-center gap-2 bg-[#09090B] rounded-sm p-1.5">
                <img src={c.theme.logo_url} alt="logo preview" className="w-10 h-10 object-contain" data-testid="theme-logo-preview" loading="lazy" decoding="async" />
                <span className="text-[10px] uppercase tracking-wider text-white/70 pr-2">Preview</span>
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-1">Primary Color</div>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={c.theme?.primary_color || "#0055FF"}
                onChange={(e) => updateTheme("primary_color", e.target.value)}
                className="h-9 w-12 rounded-sm border border-[#E4E4E7] cursor-pointer"
                data-testid="theme-primary-picker"
              />
              <Input
                value={c.theme?.primary_color || ""}
                onChange={(e) => updateTheme("primary_color", e.target.value)}
                placeholder="#0055FF"
                className="rounded-sm flex-1 font-mono text-[12px]"
                data-testid="theme-primary-hex"
              />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-1">Accent Color</div>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={c.theme?.accent_color || "#09090B"}
                onChange={(e) => updateTheme("accent_color", e.target.value)}
                className="h-9 w-12 rounded-sm border border-[#E4E4E7] cursor-pointer"
                data-testid="theme-accent-picker"
              />
              <Input
                value={c.theme?.accent_color || ""}
                onChange={(e) => updateTheme("accent_color", e.target.value)}
                placeholder="#09090B"
                className="rounded-sm flex-1 font-mono text-[12px]"
                data-testid="theme-accent-hex"
              />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-1">Font Family (optional)</div>
            <Input
              value={c.theme?.font_family || ""}
              onChange={(e) => updateTheme("font_family", e.target.value)}
              placeholder='e.g. "Poppins", sans-serif'
              className="rounded-sm"
              data-testid="theme-font-family"
            />
          </div>
        </div>
        <p className="text-[11px] text-[#71717A]">Tip: Upload your logo to any public host (Imgur, Cloudinary, your own CDN) and paste the URL here. Primary color drives buttons, links, and highlights across the app.</p>
      </Section>

      {/* Section Order */}
      <Section title="Homepage Section Order">
        <p className="text-[11px] text-[#71717A] -mt-1">Drag-free reorder — use arrows to move sections up or down on the homepage.</p>
        <div className="space-y-1.5" data-testid="section-order-list">
          {(c.section_order && c.section_order.length ? c.section_order : SECTION_KEYS).map((k, i, arr) => (
            <div key={k} className="flex items-center gap-2 p-2.5 border border-[#E4E4E7] rounded-sm bg-white" data-testid={`section-order-row-${k}`}>
              <span className="w-6 text-center text-[11px] font-mono text-[#A1A1AA]">{i + 1}</span>
              <span className="text-[13px] font-semibold text-[#09090B] flex-1">{SECTION_LABEL[k] || k}</span>
              <Button
                size="sm"
                variant="outline"
                className="rounded-sm h-7 px-2"
                disabled={i === 0}
                onClick={() => moveSection(k, -1)}
                data-testid={`section-up-${k}`}
              >
                <ArrowUp size={12} />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="rounded-sm h-7 px-2"
                disabled={i === arr.length - 1}
                onClick={() => moveSection(k, 1)}
                data-testid={`section-down-${k}`}
              >
                <ArrowDown size={12} />
              </Button>
            </div>
          ))}
        </div>
      </Section>

      {/* Section Visibility */}
      <Section title="Section Visibility (Show / Hide)">
        <div className="grid grid-cols-2 gap-3" data-testid="section-visibility">
          {[
            { key: "hero", label: "Hero / Headline" },
            { key: "filter", label: "Filter Panel" },
            { key: "premium_deals", label: "Premium Deals Section" },
            { key: "top_picks", label: "Best Deals (Top Picks)" },
            { key: "success_stories", label: "Success Stories" },
            { key: "projects", label: "Projects CTA Banner" },
            { key: "testimonials", label: "Testimonials Gallery" },
          ].map((s) => {
            const v = c.section_visibility || {};
            const checked = v[s.key] !== false; // default true
            return (
              <label key={s.key} className="flex items-center gap-2 p-2.5 border border-[#E4E4E7] rounded-sm bg-white cursor-pointer hover:border-[#0055FF]">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) =>
                    update("section_visibility", { ...(c.section_visibility || {}), [s.key]: e.target.checked })
                  }
                  data-testid={`section-toggle-${s.key}`}
                />
                <span className="text-[12px] text-[#09090B] font-semibold">{s.label}</span>
                <span className={`ml-auto text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm ${checked ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FEE2E2] text-[#991B1B]"}`}>
                  {checked ? "Shown" : "Hidden"}
                </span>
              </label>
            );
          })}
        </div>
      </Section>

      {/* Banners */}
      <Section
        title="Banners"
        action={
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => update("banners", [...(c.banners || []), emptyBanner()])} data-testid="content-add-banner">
            <Plus size={13} className="mr-1" /> Add Banner
          </Button>
        }
      >
        {(c.banners || []).length === 0 && <div className="text-[12px] text-[#A1A1AA]">No banners yet.</div>}
        {(c.banners || []).map((b, i) => (
          <div key={i} className="grid lg:grid-cols-2 gap-3 p-3 border border-[#E4E4E7] rounded-sm bg-[#FAFAFA]" data-testid={`content-banner-${i}`}>
            <Field label="Title" value={b.title} onChange={(v) => update("banners", c.banners.map((x, j) => j === i ? { ...x, title: v } : x))} testid={`banner-title-${i}`} />
            <Field label="Subtitle" value={b.subtitle} onChange={(v) => update("banners", c.banners.map((x, j) => j === i ? { ...x, subtitle: v } : x))} testid={`banner-subtitle-${i}`} />
            <Field label="Image URL" value={b.image} onChange={(v) => update("banners", c.banners.map((x, j) => j === i ? { ...x, image: v } : x))} testid={`banner-image-${i}`} />
            <Field label="CTA Text" value={b.cta_text} onChange={(v) => update("banners", c.banners.map((x, j) => j === i ? { ...x, cta_text: v } : x))} testid={`banner-cta-text-${i}`} />
            <Field label="CTA Link" value={b.cta_link} onChange={(v) => update("banners", c.banners.map((x, j) => j === i ? { ...x, cta_link: v } : x))} testid={`banner-cta-link-${i}`} />
            <div className="flex items-end">
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => update("banners", c.banners.filter((_, j) => j !== i))} data-testid={`banner-del-${i}`}>
                <Trash2 size={13} className="mr-1 text-[#DC2626]" /> Remove
              </Button>
            </div>
          </div>
        ))}
      </Section>

      {/* Sections */}
      <Section
        title="Custom Sections"
        action={
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => update("sections", [...(c.sections || []), emptySection()])} data-testid="content-add-section">
            <Plus size={13} className="mr-1" /> Add Section
          </Button>
        }
      >
        {(c.sections || []).length === 0 && <div className="text-[12px] text-[#A1A1AA]">No sections yet.</div>}
        {(c.sections || []).map((s, i) => (
          <div key={i} className="grid lg:grid-cols-2 gap-3 p-3 border border-[#E4E4E7] rounded-sm bg-[#FAFAFA]" data-testid={`content-section-${i}`}>
            <Field label="Key (id)" value={s.key} onChange={(v) => update("sections", c.sections.map((x, j) => j === i ? { ...x, key: v } : x))} testid={`section-key-${i}`} />
            <Field label="Title" value={s.title} onChange={(v) => update("sections", c.sections.map((x, j) => j === i ? { ...x, title: v } : x))} testid={`section-title-${i}`} />
            <FieldArea label="Body" value={s.body} onChange={(v) => update("sections", c.sections.map((x, j) => j === i ? { ...x, body: v } : x))} testid={`section-body-${i}`} />
            <div className="flex items-center gap-3">
              <label className="text-[11px] text-[#52525B] inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!s.enabled}
                  onChange={(e) => update("sections", c.sections.map((x, j) => j === i ? { ...x, enabled: e.target.checked } : x))}
                  data-testid={`section-enabled-${i}`}
                />
                Enabled
              </label>
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => update("sections", c.sections.filter((_, j) => j !== i))} data-testid={`section-del-${i}`}>
                <Trash2 size={13} className="mr-1 text-[#DC2626]" /> Remove
              </Button>
            </div>
          </div>
        ))}
      </Section>

      <div>
        <Button className="btn-brand rounded-sm" onClick={save} disabled={busy} data-testid="content-save-btn">
          <Save size={14} className="mr-1.5" /> {busy ? "Saving…" : "Save All Changes"}
        </Button>
      </div>
    </div>
  );
}

const Section = ({ title, action, children }) => (
  <div className="bg-white border border-[#E4E4E7] rounded-sm p-5 space-y-3">
    <div className="flex items-center justify-between">
      <h3 className="font-heading font-semibold text-lg tracking-tight">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const Field = ({ label, value, onChange, testid, placeholder }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-1">{label}</div>
    <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-sm" data-testid={testid} placeholder={placeholder} />
  </div>
);

const FieldArea = ({ label, value, onChange, testid }) => (
  <div className="lg:col-span-2">
    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-1">{label}</div>
    <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-sm" rows={2} data-testid={testid} />
  </div>
);
